import { DatePipe, NgFor } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit } from "@angular/core";
import { RouterLink } from "@angular/router";
import { firstValueFrom } from "rxjs";
import { ConfigService } from "src/app/servicios/config/config.service";
import { KebabCasePipe } from "../../../pipes/kebab-case.pipe";
import { BlogService } from "@src/app/servicios/blog/blog.service";
import { DetalleBlog } from "@src/app/models/interfaces/blog.interface";

@Component({
  selector: "app-bloque-ultimos-blogs",
  templateUrl: "./bloque-ultimos-blogs.component.html",
  styleUrls: ["./bloque-ultimos-blogs.component.css"],
  imports: [RouterLink, DatePipe, KebabCasePipe, NgFor],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [BlogService],
})
/**
 * Componente que muestra los cuatro últimos blogs publicados.
 */
export class BloqueUltimosBlogsComponent implements OnInit {
  // Variables principales
  constantesGlobales: any; // Configuración global de la aplicación
  posts: any[] = []; // Lista de posts del blog
  fechaHoy: number; // Fecha actual en milisegundos

  constructor(
    private blogService: BlogService,
    private configService: ConfigService,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {}

  /**
   * Método del ciclo de vida de Angular que se ejecuta al inicializar el componente.
   *
   * - Establece la fecha actual en la propiedad `fechaHoy`.
   * - Obtiene las constantes globales desde el servicio de configuración y las asigna a la propiedad `constantesGlobales`.
   * - Extrae el identificador de ámbito (`idAmbito`) de las constantes globales y lo utiliza para
   *   realizar una solicitud de los últimos blogs mediante el método `fetchBlogPosts`.
   * - Llama al método `fetchConfigPrensa` para obtener la configuración relacionada con la prensa.
   */
  ngOnInit(): void {
    this.fechaHoy = Date.now();
    this.constantesGlobales = this.configService.getConstantesGlobales();
    const idAmbito = this.constantesGlobales.idAmbito;
    this.fetchBlogPosts(idAmbito);
    this.fetchConfigPrensa();
  }

  /**
   * Obtiene y procesa las publicaciones de blog asociadas a un ámbito específico.
   *
   * @param idAmbito - El identificador del ámbito para filtrar las publicaciones del blog.
   * @returns Una promesa que se resuelve cuando las publicaciones del blog han sido obtenidas y procesadas.
   *
   * @remarks
   * Este método utiliza el servicio `blogService` para obtener las publicaciones del blog
   * relacionadas con el ámbito especificado. Una vez obtenidas, se asignan a la propiedad `posts`
   * los datos correspondientes a los videos. Finalmente, se solicita la detección de cambios
   * en el componente para reflejar los datos actualizados.
   *
   * @throws Puede lanzar un error si la promesa de `firstValueFrom` falla o si no se pueden obtener
   * las publicaciones del blog.
   */
  async fetchBlogPosts(idAmbito: string): Promise<void> {
    const blogPost = this.blogService.getPostsBlog(4, idAmbito, "ambito");
    if (blogPost) {
      const detalleBlog: DetalleBlog = await firstValueFrom(blogPost);
      this.posts = detalleBlog["video"];
      this.cdr.detectChanges();
    }
  }

  /**
   * Obtiene la configuración de prensa desde el servicio `configService` y aplica los colores CSS
   * definidos en las constantes globales de la configuración general.
   *
   * @returns {Promise<void>} Una promesa que se resuelve cuando la configuración ha sido obtenida
   * y los colores CSS han sido aplicados.
   *
   * @remarks
   * - Utiliza `firstValueFrom` para obtener el valor de un observable de configuración.
   * - Si no existe una configuración de prensa disponible, no realiza ninguna acción.
   */
  async fetchConfigPrensa(): Promise<void> {
    const configPrensa = this.configService.getConfigPrensa();
    if (configPrensa) {
      const resultConfig = await firstValueFrom(configPrensa);
      this.setCssColors(resultConfig.general["global-constants"]);
    }
  }

  /**
   * setCssColors:
   * - Aplica el color de acento definido en las constantes globales al componente.
   * @param globalConstants Constantes globales con la configuración de colores.
   */
  setCssColors(globalConstants): void {
    if (globalConstants.accent_color) {
      this.elementRef.nativeElement.style.setProperty("--accent-color", globalConstants.accent_color);
    }
  }

  trackPost(item: any): any {
    return item?.id;
  }
}
